import { useMemo } from "react";
import { useQuery } from "react-query";

type HybrisFeatureBool = "Y" | "N";

interface HybrisProductData {
  bluetooth: HybrisFeatureBool;
  code: string;
  description: string;
  imageURL: string;
  lastUpdated: string; // date in string
  mediumImageURL: string;
  name: string;
  price: {
    currencyIso: "USD" | "EUR"; // this has more options, and can be extended
    formattedMonthlyRate: string; // $54.84
    formattedValue: string; // $329.00
    monthlyRate: number; // 54
    value: number; // 329
  };
  productPageURL: string;
  shortName: string;
  smallImageURL: string;
  variantOptions: {
    code: string;
    colour?: {
      code: string;
      colourGroup: string;
      description: string;
      hexadecimalCodes: string;
    };
    defaultVariantProduct: boolean;
    ean: string;
    imageURL: string;
    mediumImageURL: string;
    productPageURL: string;
    shortName: string;
  }[];
  wifi: HybrisFeatureBool;
  wireless: HybrisFeatureBool;
}

const useGetProduct = () => {
  const IGNORED_VARIANTS = ["bose_frames_alto_sm_black"];
  const NAME_PATCHES: { [key: string]: string } = {
    productshortname_noise_masking_sleepbuds_ii_bose_0: "Bose Sleepbuds™ II",
  };

  const productId: string = useMemo(() => {
    if (process.env.NODE_ENV === "development") {
      return "qc45";
    }

    return (window as any).Bose.DataLayer.Data.Product.productCode;
  }, []);

  const data = useQuery<HybrisProductData>(
    ["product", productId],
    async () => {
      const endpoint = `https://cdn.prod.gds.bose.com/product-api/v1/b2c/website_ce_us/us/en/products/${productId}?fields=BASIC`;
      const res = await fetch(endpoint);
      const jsonRes: HybrisProductData = { ...(await res.json()) };
      jsonRes.variantOptions = jsonRes.variantOptions
        .filter((variantOption) => {
          if (IGNORED_VARIANTS.includes(variantOption.code)) {
            return false;
          }

          return variantOption;
        })
        .map((variantOption) => {
          if (NAME_PATCHES[variantOption.shortName]) {
            variantOption.shortName = NAME_PATCHES[variantOption.shortName];
          }

          return variantOption;
        });

      if (NAME_PATCHES[jsonRes.shortName]) {
        jsonRes.shortName = NAME_PATCHES[jsonRes.shortName];
      }

      return jsonRes;
    },
    { enabled: !!productId }
  );

  return data;
};

export default useGetProduct;
